import { LoggingLevel } from '@augloop/interfaces';
import { PerformanceEventType } from './performance-event-type';
const serviceName = ((typeof process != 'undefined') && process.env) ? process.env.SERVICE_NAME : 'client';
const symbolSpace = 'abcdefghijklmnopqrstuvwxyz0123456789';
const symbolSpaceLookup = {
    97: 0,
    98: 1,
    99: 2,
    100: 3,
    101: 4,
    102: 5,
    103: 6,
    104: 7,
    105: 8,
    106: 9,
    107: 10,
    108: 11,
    109: 12,
    110: 13,
    111: 14,
    112: 15,
    113: 16,
    114: 17,
    115: 18,
    116: 19,
    117: 20,
    118: 21,
    119: 22,
    120: 23,
    121: 24,
    122: 25,
    48: 26,
    49: 27,
    50: 28,
    51: 29,
    52: 30,
    53: 31,
    54: 32,
    55: 33,
    56: 34,
    57: 35
};
let loggers = [];
let decidingLoggers = [];
let getCorrelation;
// Performance Event is meant to work only server-side, so we shouldn't have explicit dependency on it here
let startPerformanceEvent = (_name) => null;
let stopPerformanceEvent = (_event) => { };
const aggregators = new Map();
const loggersByLevel = new Map();
const decidingLoggersByLevel = new Map();
const overridenTags = new Map();
export var Log;
(function (Log) {
    Log.defineCoreLogCategory = (name) => {
        return { root: 'Core', name: name };
    };
    Log.defineWorkflowLogCategory = (name) => {
        return { root: 'Workflow', name: name };
    };
    Log.clearLoggers = () => {
        loggers = [];
        decidingLoggers = [];
        loggersByLevel.clear();
        decidingLoggersByLevel.clear();
    };
    Log.clearAggregators = () => {
        aggregators.clear();
    };
    Log.addLogger = (logger) => {
        if (loggers.indexOf(logger) !== -1) {
            return;
        }
        loggers.push(logger);
        pushToMapByLevel(loggersByLevel, logger);
    };
    Log.addDecidingLogger = (logger) => {
        if (decidingLoggers.indexOf(logger) !== -1) {
            return;
        }
        decidingLoggers.push(logger);
        pushToMapByLevel(decidingLoggersByLevel, logger);
    };
    Log.setCorrelationContextCallback = (cb) => {
        getCorrelation = cb;
    };
    Log.setStartPerformanceEventCallback = (cb) => {
        startPerformanceEvent = cb;
    };
    Log.setStopPerformanceEventCallback = (cb) => {
        stopPerformanceEvent = cb;
    };
    Log.addAggregator = (aggregator) => {
        aggregator.init((obj, isAggregated) => {
            logToLoggers(obj, isAggregated);
        });
        // Group aggregators by eventName
        if (aggregators.has(aggregator.eventName)) {
            aggregators.get(aggregator.eventName).push(aggregator);
        }
        else {
            aggregators.set(aggregator.eventName, [aggregator]);
        }
    };
    Log.flushAggregators = (stop) => {
        aggregators.forEach((aggregatorList) => {
            aggregatorList.forEach(aggregator => aggregator.flush(stop));
        });
    };
    /**
     * Log a specific tag at a different level
     * @param tag the string encoded log tag. Use {@link tagIdToString} to convert from its integer form
     * @param level The new level to log the tag at.
     *
     * @example
     * const tag = 0x13371337;
     * Log.setTagLevelOverride(tagIdToString(tag), LoggingLevel.Error);
     * Log.info(tag, LogCategory.WorkflowDefault, 'This is logged at error level!');
     */
    Log.setTagLevelOverride = (tag, level) => {
        const tagId = tagStringToId(tag);
        overridenTags.set(tagId, level);
    };
    Log.resetTagLevelOverrides = () => {
        overridenTags.clear();
    };
    /** Logs a message at the `error` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.error(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.error = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.error, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `warn` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.warn(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.warn = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.warn, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `info` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.info(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.info = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.info, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `verbose` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.verbose(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.verbose = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.verbose, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `debug` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.debug(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.debug = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.debug, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `metric` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.metric(0, LogCategory.WorkflowDefault, 'Hello world');
     * Log.metric(0, LogCategory.WorkflowMetricsOnly,
     *          {
     *               MetricName: {
     *                   dimensionNames: () => ['Dimension1', 'Dimension2', 'AndSoOn'],
     *                   dimensionValues: ['DimensionValue1', 'DimensionValue2', 'DimensionValue3'],
     *                   value: 1
     *               }
     *           });
     *
     */
    Log.metric = (tagId, logCategory, a, b, c, d, e, f, g, h) => {
        log(tagId, logCategory, LoggingLevel.metric, a, b, c, d, e, f, g, h);
    };
    Log.formatMetric = (name, value, dimensionNames, dimensionValues) => {
        const result = {};
        result[name] = {
            dimensionNames: dimensionNames,
            dimensionValues: dimensionValues,
            value: value
        };
        return result;
    };
    Log.dynamic = (obj) => {
        logToLoggers(obj);
    };
})(Log || (Log = {}));
const pushToMapByLevel = (map, logger) => {
    const targetLevel = logger.level;
    // InternetExplorer does not support `Object.values`, emulating via `keys.map`
    Object.keys(LoggingLevel)
        .map(key => LoggingLevel[key])
        .filter((level) => typeof level !== 'string')
        .filter(level => level <= targetLevel)
        .forEach(level => {
        if (!map.has(level)) {
            map.set(level, [logger]);
        }
        else {
            map.get(level)
                .push(logger);
        }
    });
};
const unfoldArgs = (a, b, c, d, e, f, g, h) => {
    // Optimize for the case of a single argument so as not to construct arrays unnecessarily
    if (b === undefined && (typeof a === 'string' || typeof a === 'object')) {
        return a;
    }
    if (b === undefined && typeof a === 'function') {
        return a();
    }
    const result = [];
    for (const arg of [a, b, c, d, e, f, g, h]) {
        if (arg !== undefined) {
            result.push(typeof arg === 'function' ? arg() : arg);
        }
    }
    return result;
};
export const argsToString = (args) => {
    if (typeof args === 'string') {
        return args;
    }
    let result = '';
    for (let i = 0; i < args.length; i++) {
        if (i > 0) {
            result += ' ';
        }
        const arg = args[i];
        if (arg instanceof Error) {
            result += JSON.stringify({
                message: arg.message,
                name: arg.name,
                stack: arg.stack
            });
        }
        else if (typeof arg === 'object') {
            result += JSON.stringify(arg);
        }
        else {
            result += arg;
        }
    }
    return result;
};
const emptyArray = [];
const traceEventMetricDimensions = ['Level', 'Tag'];
const workflowTraceEventMetricDimensions = ['Level', 'Tag', 'Workflow'];
export const getNumLoggers = (level) => {
    return getLoggers(level).length;
};
const getLoggers = (level) => {
    if (level !== undefined) {
        return loggersByLevel.get(level) || emptyArray;
    }
    return loggers;
};
const getDecidingLoggers = (level, firstArg) => {
    const loggers = level !== undefined ? (decidingLoggersByLevel.get(level) || []) : decidingLoggers;
    return loggers.filter(logger => logger.shouldLog(firstArg));
};
const log = (tagId, category, level, a, b, c, d, e, f, g, h) => {
    // Expensive log statements will be logged with () => '' syntax and should not be unnecessarily unfolded.
    level = overridenTags.get(tagId) || level;
    const normalLoggers = getLoggers(level);
    const decidingLoggers = getDecidingLoggers(level, a);
    if (normalLoggers.length == 0 && decidingLoggers.length == 0) {
        return;
    }
    const event = startPerformanceEvent(PerformanceEventType.Log);
    const tag = tagIdToString(tagId);
    const unfoldedArgs = unfoldArgs(a, b, c, d, e, f, g, h);
    if (logCategoryIsMetricsOnly(category)) {
        if (isObjectAndNotAnArray(unfoldedArgs)) {
            logToLoggers({
                eventName: 'Metrics',
                tagId: tag,
                category: `${category.root}.${category.name}`,
                traceLevel: level,
                message: '',
                getMetrics: () => unfoldedArgs
            }, false, normalLoggers, decidingLoggers);
        }
        else {
            // Incorrect arguments for metrics-only record
        }
    }
    else if (isObjectAndNotAnArray(unfoldedArgs)) {
        const obj = unfoldedArgs;
        obj.tagId = tag;
        obj.category = `${category.root}.${category.name}`;
        if (obj.eventName === 'Operation' && category.root === 'Workflow') {
            obj.eventName = 'WorkflowOperation';
            if (getCorrelation) {
                obj.joinContextId = getCorrelation().joinContextId;
                obj.workflow = getCorrelation().workflow;
            }
        }
        obj.traceLevel = level;
        const aggregatorList = aggregators.get(obj.eventName);
        if (aggregatorList) {
            for (let index = 0; index < aggregatorList.length; ++index) {
                const aggregator = aggregatorList[index];
                if (aggregator && aggregator.add(obj, index === aggregatorList.length - 1)) {
                    // once we find an aggregator that matches condition for this log, break the loop to avoid any dup logging
                    break;
                }
            }
        }
        else {
            logToLoggers(obj, false, normalLoggers, decidingLoggers);
        }
    }
    else {
        const getMetrics = () => {
            var _a;
            if (category.root === 'Core') {
                return {
                    TraceEventV2: {
                        dimensionNames: () => traceEventMetricDimensions,
                        dimensionValues: [String(level), tag],
                        value: 1
                    }
                };
            }
            else {
                return {
                    WorkflowTraceEvent: {
                        dimensionNames: () => workflowTraceEventMetricDimensions,
                        dimensionValues: [String(level), tag, getCorrelation ? (_a = getCorrelation()) === null || _a === void 0 ? void 0 : _a.workflow : ''],
                        value: 1
                    }
                };
            }
        };
        logToLoggers({
            eventName: 'Log',
            tagId: tag,
            category: `${category.root}.${category.name}`,
            traceLevel: level,
            message: argsToString(unfoldedArgs),
            getMetrics
        }, false, normalLoggers, decidingLoggers);
    }
    stopPerformanceEvent(event);
};
const logToLoggers = (obj, isAggregated = false, loggers, moreLoggers) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    if (loggers == null) {
        loggers = getLoggers(obj.traceLevel);
    }
    moreLoggers = moreLoggers || emptyArray;
    let logOptions;
    if (loggers.length > 0 || moreLoggers.length > 0) {
        obj.serviceName = serviceName;
        if (!isAggregated && getCorrelation) {
            const correlation = getCorrelation();
            if (correlation) {
                logOptions = {
                    disableLogging: correlation.disableLogging,
                    userDataBoundaryType: (_b = (_a = correlation.sessionDescriptor) === null || _a === void 0 ? void 0 : _a.userContext) === null || _b === void 0 ? void 0 : _b.userDataBoundaryType
                };
                obj.cv = obj.cv ? obj.cv : correlation.cv.toString();
                obj.sessionKey = obj.sessionKey ? obj.sessionKey : correlation.sessionKey;
                obj.userTenantId = obj.userTenantId ? obj.userTenantId : correlation.userTenantId;
                obj.workflow = obj.workflow ? obj.workflow : correlation.workflow;
                // Note, we exclude clientFlights from automatic stamping as its length is not well bounded and it is expensive to include on every log
                // (use setClientMetadata to include all fields, including clientFlights, in a specific log if needed)
                obj.clientAppName = obj.clientAppName ? obj.clientAppName : (_c = correlation.clientMetadata) === null || _c === void 0 ? void 0 : _c.appName;
                obj.clientAppPlatform = obj.clientAppPlatform ? obj.clientAppPlatform : (_d = correlation.clientMetadata) === null || _d === void 0 ? void 0 : _d.appPlatform;
                obj.clientAppVersion = obj.clientAppVersion ? obj.clientAppVersion : (_e = correlation.clientMetadata) === null || _e === void 0 ? void 0 : _e.appVersion;
                obj.clientDeviceId = obj.clientDeviceId ? obj.clientDeviceId : (_f = correlation.clientMetadata) === null || _f === void 0 ? void 0 : _f.deviceId;
                obj.clientDocSessionId = obj.clientDocSessionId ? obj.clientDocSessionId : (_g = correlation.clientMetadata) === null || _g === void 0 ? void 0 : _g.docSessionId;
                obj.clientReleaseAudienceGroup = obj.clientReleaseAudienceGroup ? obj.clientReleaseAudienceGroup : (_h = correlation.clientMetadata) === null || _h === void 0 ? void 0 : _h.releaseAudienceGroup;
                obj.clientReleaseChannel = obj.clientReleaseChannel ? obj.clientReleaseChannel : (_j = correlation.clientMetadata) === null || _j === void 0 ? void 0 : _j.releaseChannel;
                obj.clientReleaseFork = obj.clientReleaseFork ? obj.clientReleaseFork : (_k = correlation.clientMetadata) === null || _k === void 0 ? void 0 : _k.releaseFork;
                obj.clientRuntimeVersion = obj.clientRuntimeVersion ? obj.clientRuntimeVersion : (_l = correlation.clientMetadata) === null || _l === void 0 ? void 0 : _l.runtimeVersion;
                obj.clientSessionId = obj.clientSessionId ? obj.clientSessionId : (_m = correlation.clientMetadata) === null || _m === void 0 ? void 0 : _m.sessionId;
                obj.clientUserAgent = obj.clientUserAgent ? obj.clientUserAgent : (_o = correlation.clientMetadata) === null || _o === void 0 ? void 0 : _o.userAgent;
                obj.traceId = obj.traceId || correlation.traceId;
                obj.isClientTelemetrySampled = obj.isClientTelemetrySampled ? obj.isClientTelemetrySampled : (_p = correlation.clientMetadata) === null || _p === void 0 ? void 0 : _p.isClientTelemetrySampled;
            }
        }
        for (const logger of loggers) {
            logger.log(obj, logOptions);
        }
        for (const logger of moreLoggers) {
            logger.log(obj);
        }
    }
};
const logCategoryIsMetricsOnly = (category) => {
    return category.name === LogCategory.WorkflowMetricsOnly.name && category.root === LogCategory.WorkflowMetricsOnly.root;
};
const isObjectAndNotAnArray = (obj) => {
    return !Array.isArray(obj) && typeof obj === 'object';
};
/**
 * converts a generated numberic tag to its encoded form to be used in logs
 *
 * @note Do not assign numeric tags manually - use `0` as a placeholder and let
 * the pipeline assign correct tags.
 */
export const tagIdToString = (tagId) => {
    // 0x3F covers a base64 range, but only safe tags should ever be generated
    return symbolSpace[(tagId >> 24) & 0x3F]
        + symbolSpace[(tagId >> 18) & 0x3F]
        + symbolSpace[(tagId >> 12) & 0x3F]
        + symbolSpace[(tagId >> 6) & 0x3F]
        + symbolSpace[(tagId >> 0) & 0x3F];
};
const tagStringToId = (tag) => {
    if (tag && tag.length === 5) {
        return (symbolSpaceLookup[tag.charCodeAt(0)] << 24
            | symbolSpaceLookup[tag.charCodeAt(1)] << 18
            | symbolSpaceLookup[tag.charCodeAt(2)] << 12
            | symbolSpaceLookup[tag.charCodeAt(3)] << 6
            | symbolSpaceLookup[tag.charCodeAt(4)]);
    }
    return -1;
};
export class LogCategory {
}
LogCategory.CoreDefault = Log.defineCoreLogCategory('Default');
LogCategory.CoreSystem = Log.defineCoreLogCategory('System');
LogCategory.CoreUnsampled = Log.defineCoreLogCategory('Unsampled');
LogCategory.WorkflowDefault = Log.defineWorkflowLogCategory('Default');
LogCategory.WorkflowUnsampled = Log.defineWorkflowLogCategory('Unsampled');
LogCategory.WorkflowMetricsOnly = Log.defineWorkflowLogCategory('MetricsOnly');
LogCategory.PrivacyGuardEvent = Log.defineCoreLogCategory('PrivacyGuardEvent');
//# sourceMappingURL=logging.js.map